/**
 * Portal Routes
 * Routes for parent and teacher portals
 */

import { lazy } from "react";
import { RouteObject } from "react-router-dom";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import {
  StaffOnParentInvoice,
  StaffOnParentStatements,
} from "@/components/auth/EmailLinkRedirects";
import { AppShell } from "@/components/layout/AppShell";
import { LazyPage } from "../helpers";

// Parent Portal
const ParentDashboardPage = lazy(() => import("@/pages/parent").then(m => ({ default: m.ParentDashboardPage })));
const MyStudentsPage = lazy(() => import("@/pages/parent").then(m => ({ default: m.MyStudentsPage })));
const ParentStudentDetailPage = lazy(() => import("@/pages/parent").then(m => ({ default: m.ParentStudentDetailPage })));
const ParentInvoicesPage = lazy(() => import("@/pages/parent").then(m => ({ default: m.ParentInvoicesPage })));
const ParentInvoiceDetailPage = lazy(() => import("@/pages/parent").then(m => ({ default: m.ParentInvoiceDetailPage })));
const ParentPaymentsPage = lazy(() => import("@/pages/parent").then(m => ({ default: m.ParentPaymentsPage })));
const ParentPaymentDetailPage = lazy(() => import("@/pages/parent").then(m => ({ default: m.ParentPaymentDetailPage })));
const MakePaymentPage = lazy(() => import("@/pages/parent").then(m => ({ default: m.MakePaymentPage })));
const ParentStatementsPage = lazy(() => import("@/pages/parent").then(m => ({ default: m.ParentStatementsPage })));
const ParentApplicationsPage = lazy(() => import("@/pages/parent").then(m => ({ default: m.ParentApplicationsPage })));
const ParentApplicationDetailPage = lazy(() => import("@/pages/parent").then(m => ({ default: m.ParentApplicationDetailPage })));
const SubmitApplicationPage = lazy(() => import("@/pages/parent").then(m => ({ default: m.SubmitApplicationPage })));
const AddStudentApplicationPage = lazy(() => import("@/pages/parent/AddStudentApplicationPage").then(m => ({ default: m.AddStudentApplicationPage })));
const PaymentMethodsPage = lazy(() => import("@/pages/parent").then(m => ({ default: m.PaymentMethodsPage })));
const ParentAddPaymentMethodPage = lazy(() => import("@/pages/parent").then(m => ({ default: m.ParentAddPaymentMethodPage })));
const ProfileSettingsPage = lazy(() => import("@/pages/parent").then(m => ({ default: m.ProfileSettingsPage })));
const ParentAttendancePage = lazy(() => import("@/pages/parent").then(m => ({ default: m.ParentAttendancePage })));
// Hifdh Progress v2 (the v1 page goes with v1 in the last Hifdh PR).
const ParentHifdhPage = lazy(() => import("@/pages/hifdh/v2").then(m => ({ default: m.ParentHifdhPage })));
const ParentReportCardPage = lazy(() => import("@/pages/hifdh/v2").then(m => ({ default: m.ParentReportCardPage })));
const ParentCertificatePage = lazy(() => import("@/pages/hifdh/v2").then(m => ({ default: m.ParentCertificatePage })));
const ParentLessonProgressPage = lazy(() => import("@/pages/parent/ParentLessonProgressPage").then(m => ({ default: m.ParentLessonProgressPage })));
const ParentAssignmentsPage = lazy(() => import("@/pages/parent/ParentAssignmentsPage").then(m => ({ default: m.ParentAssignmentsPage })));
const ParentPerformanceSummaryPage = lazy(() => import("@/pages/parent/ParentPerformanceSummaryPage").then(m => ({ default: m.ParentPerformanceSummaryPage })));

// Teacher Portal
const TeacherDashboardPage = lazy(() => import("@/pages/teacher").then(m => ({ default: m.TeacherDashboardPage })));
const TeacherMyClassesPage = lazy(() => import("@/pages/teacher").then(m => ({ default: m.TeacherMyClassesPage })));
const TeacherMyStudentsPage = lazy(() => import("@/pages/teacher").then(m => ({ default: m.TeacherMyStudentsPage })));
const TeacherClassDetailPage = lazy(() => import("@/pages/teacher").then(m => ({ default: m.TeacherClassDetailPage })));
const TeacherLessonPlansPage = lazy(() => import("@/pages/teacher/TeacherLessonPlansPage").then(m => ({ default: m.TeacherLessonPlansPage })));

export const parentPortalRoutes: RouteObject[] = [
  {
    path: "/parent/dashboard",
    element: (
      <ProtectedRoute roles={["parent"]}>
        <AppShell>
          <LazyPage><ParentDashboardPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/parent/students",
    element: (
      <ProtectedRoute roles={["parent"]}>
        <AppShell>
          <LazyPage><MyStudentsPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/parent/students/:studentId",
    element: (
      <ProtectedRoute roles={["parent"]}>
        <AppShell>
          <LazyPage><ParentStudentDetailPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/parent/invoices",
    element: (
      <ProtectedRoute roles={["parent"]}>
        <AppShell>
          <LazyPage><ParentInvoicesPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/parent/invoices/:invoiceId",
    element: (
      <StaffOnParentInvoice>
        <ProtectedRoute roles={["parent"]}>
          <AppShell>
            <LazyPage><ParentInvoiceDetailPage /></LazyPage>
          </AppShell>
        </ProtectedRoute>
      </StaffOnParentInvoice>
    ),
  },
  {
    path: "/parent/payments",
    element: (
      <ProtectedRoute roles={["parent"]}>
        <AppShell>
          <LazyPage><ParentPaymentsPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/parent/payments/make",
    element: (
      <ProtectedRoute roles={["parent"]}>
        <AppShell>
          <LazyPage><MakePaymentPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/parent/payments/:paymentId",
    element: (
      <ProtectedRoute roles={["parent"]}>
        <AppShell>
          <LazyPage><ParentPaymentDetailPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/parent/statements",
    element: (
      <StaffOnParentStatements>
        <ProtectedRoute roles={["parent"]}>
          <AppShell>
            <LazyPage><ParentStatementsPage /></LazyPage>
          </AppShell>
        </ProtectedRoute>
      </StaffOnParentStatements>
    ),
  },
  {
    path: "/parent/payment-methods",
    element: (
      <ProtectedRoute roles={["parent"]}>
        <AppShell>
          <LazyPage><PaymentMethodsPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/parent/payment-methods/add",
    element: (
      <ProtectedRoute roles={["parent"]}>
        <AppShell>
          <LazyPage><ParentAddPaymentMethodPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/parent/profile",
    element: (
      <ProtectedRoute roles={["parent"]}>
        <AppShell>
          <LazyPage><ProfileSettingsPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/parent/applications",
    element: (
      <ProtectedRoute roles={["parent"]}>
        <AppShell>
          <LazyPage><ParentApplicationsPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/parent/applications/new",
    element: (
      <ProtectedRoute roles={["parent"]}>
        <AppShell>
          <LazyPage><SubmitApplicationPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/parent/applications/add-student",
    element: (
      <ProtectedRoute roles={["parent"]}>
        <AppShell>
          <LazyPage><AddStudentApplicationPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/parent/applications/:applicationId",
    element: (
      <ProtectedRoute roles={["parent"]}>
        <AppShell>
          <LazyPage><ParentApplicationDetailPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/parent/attendance",
    element: (
      <ProtectedRoute roles={["parent"]}>
        <AppShell>
          <LazyPage><ParentAttendancePage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/parent/hifdh-progress",
    element: (
      <ProtectedRoute roles={["parent"]}>
        <AppShell>
          <LazyPage><ParentHifdhPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/parent/hifdh-progress/:studentId/report-cards/:month",
    element: (
      <ProtectedRoute roles={["parent"]}>
        <AppShell>
          <LazyPage><ParentReportCardPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/parent/hifdh-progress/:studentId/certificates/:certificateId",
    element: (
      <ProtectedRoute roles={["parent"]}>
        <AppShell>
          <LazyPage><ParentCertificatePage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/parent/lesson-progress",
    element: (
      <ProtectedRoute roles={["parent"]}>
        <AppShell>
          <LazyPage><ParentLessonProgressPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/parent/assignments",
    element: (
      <ProtectedRoute roles={["parent"]}>
        <AppShell>
          <LazyPage><ParentAssignmentsPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/parent/performance",
    element: (
      <ProtectedRoute roles={["parent"]}>
        <AppShell>
          <LazyPage><ParentPerformanceSummaryPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
];

export const teacherPortalRoutes: RouteObject[] = [
  {
    path: "/teacher/dashboard",
    element: (
      <ProtectedRoute roles={["teacher"]}>
        <AppShell>
          <LazyPage><TeacherDashboardPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/teacher/classes",
    element: (
      <ProtectedRoute roles={["teacher"]}>
        <AppShell>
          <LazyPage><TeacherMyClassesPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/teacher/classes/:id",
    element: (
      <ProtectedRoute roles={["teacher"]}>
        <AppShell>
          <LazyPage><TeacherClassDetailPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/teacher/students",
    element: (
      <ProtectedRoute roles={["teacher"]}>
        <AppShell>
          <LazyPage><TeacherMyStudentsPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/teacher/lesson-plans",
    element: (
      <ProtectedRoute roles={["teacher", "admin", "principal", "staff"]}>
        <AppShell>
          <LazyPage><TeacherLessonPlansPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
];
